/**
 * TdoNoteBuilderComponent is responsible for managing a note builder interface in Angular.
 * It allows users to create, edit, delete, and reorder predefined notes and note sets.
 * The component utilizes Angular Material's drag-and-drop feature for rearranging items.
 * It also handles asynchronous interactions with a backend service for data persistence.
 */

import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CdkDragDrop, moveItemInArray, CdkDragStart, CdkDragEnd } from '@angular/cdk/drag-drop';
import { NotesService } from '../../../services/notes.service';
import { PredefinedNoteDataModel } from '../../../models/notes/predefined-note-data.model';
import { PredefinedNoteSetDataModel } from '../../../models/notes/predefined-note-set-data.model';
import { MessagingService } from '../../../services/messaging.service';
import { OrderChangeModel } from '../../../models/ui/OrderChangeModel';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DialogService } from '../../../services/dialog.service';
import { forkJoin, Subscription } from 'rxjs';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Inject } from '@angular/core';

@Component({
  selector: 'app-tdo-note-builder',
  templateUrl: './tdo-note-builder.component.html',
  styleUrls: ['./tdo-note-builder.component.scss']
})
export class TdoNoteBuilderComponent implements OnInit, OnDestroy {
  
  // Selected data note set and related notes
  selectedDataNoteSet: PredefinedNoteSetDataModel;
  dataNoteSets: PredefinedNoteSetDataModel[] = [];
  selectedPredefinedNotes: PredefinedNoteDataModel[] = [];
  
  // Loading indicator
  isLoading = false;

  // Note data storage
  noteData: string = '';
  selectedNotes: PredefinedNoteDataModel[] = [];
  editableNote: PredefinedNoteDataModel;

  // Buffer for change tracking
  private changesBuffer: OrderChangeModel[] = [];
  private inactivityTimeout: any;
  private subscriptions: Subscription = new Subscription();

  // Dragging state flag
  isDragging = false;

  constructor(
    private notesService: NotesService,
    private messagingService: MessagingService,
    private dialogService: DialogService,
    private dialogRef: MatDialogRef<TdoNoteBuilderComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any, //Receives the dialogue data
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef // Inject ChangeDetectorRef
  ) { }

  ngOnInit(): void {

    if (this.data && this.data.Note) {
      this.noteData = this.data.Note; // Take the text of the note
    } else {
      console.warn('The information to initialize noteData was not received');
    }

    this.isLoading = true; // Show loading indicator
    this.fetchBuilderNotes()
      .then((result: PredefinedNoteSetDataModel[]) => {
        this.dataNoteSets = result;

        // Set the first note set as selected if available
        if (this.dataNoteSets.length > 0) {
          this.selectedDataNoteSet = this.dataNoteSets[0];
          this.selectedPredefinedNotes = this.selectedDataNoteSet.dataPredefinedNotes.sort(
            (a, b) => a.NoteOrder - b.NoteOrder
          );
        }

        this.isLoading = false;
      })
      .catch(err => {
        console.error('Error fetching builder notes:', err);
        this.isLoading = false;
        this.messagingService.sendComponentMessage('notesDisplayWarning', {
          message: 'Whoops... something went wrong'
        });
      });
  }

  ngOnDestroy() {
    // Clear inactivity timeout on component destroy
    if (this.inactivityTimeout) {
      clearTimeout(this.inactivityTimeout);
    }
    // Unsubscribe from any active subscriptions
    this.subscriptions.unsubscribe();
  }

  /**
   * Fetches predefined note sets from the service.
   * @returns A promise resolving to an array of PredefinedNoteSetDataModel.
   */
  fetchBuilderNotes() {
    const promise = new Promise<PredefinedNoteSetDataModel[]>((resolve, reject) => {
      this.notesService.getNoteBuilderSets()
        .subscribe(response => {
          resolve(response);
        }, err => {
          reject(err);
        });
    });
    return promise;
  }

  // Handles note drag start event
  onNoteDragStarted(event: CdkDragStart, note: PredefinedNoteDataModel) {
    this.isDragging = true;
    this.selectedNotes = [note];
    this.cdr.detectChanges();
  }

  // Handles note drag end event
  onNoteDragEnded(event: CdkDragEnd) {
    this.isDragging = false;
    this.resetInactivityTimeout();
  }

  // Handles note set drag start event
  onNoteSetDragStarted(event: CdkDragStart, noteSet: PredefinedNoteSetDataModel) {
    this.isDragging = true;
    this.selectedDataNoteSet = noteSet;
    this.selectedPredefinedNotes = noteSet.dataPredefinedNotes.sort((a, b) => a.NoteOrder - b.NoteOrder);
    this.cdr.detectChanges();
  }

  // Handles note set drag end event
  onNoteSetDragEnded(event: CdkDragEnd) {
    this.isDragging = false;
    this.resetInactivityTimeout();
  }

  // Handles dropping of note sets
  sectionDrop(event: CdkDragDrop<PredefinedNoteSetDataModel[]>) {
    moveItemInArray(this.dataNoteSets, event.previousIndex, event.currentIndex);

    // Update local order
    this.dataNoteSets.forEach((noteSet, index) => {
      noteSet.NoteSetOrder = index;
    });

    // Add change to buffer
    const model = new OrderChangeModel();
    model.newOrder = event.currentIndex;
    model.oldOrder = event.previousIndex;
    model.id = this.dataNoteSets[event.currentIndex].NoteSetId;

    this.changesBuffer.push(model);
  }

  // Handles dropping of notes
  noteDrop(event: CdkDragDrop<PredefinedNoteDataModel[]>) {
    moveItemInArray(this.selectedPredefinedNotes, event.previousIndex, event.currentIndex);

    // Update local order
    this.selectedPredefinedNotes.forEach((note, index) => {
      note.NoteOrder = index;
    });

    // Add change to buffer
    const model = new OrderChangeModel();
    model.newOrder = event.currentIndex;
    model.oldOrder = event.previousIndex;
    model.id = this.selectedPredefinedNotes[event.currentIndex].PredefinedNoteId;
    model.parentId = this.selectedDataNoteSet.NoteSetId;

    this.changesBuffer.push(model);
  }

  // Resets the inactivity timeout
  resetInactivityTimeout() {
    if (this.inactivityTimeout) {
      clearTimeout(this.inactivityTimeout);
    }

    this.inactivityTimeout = setTimeout(() => {
      if (!this.isDragging) {
        this.processChangesBuffer();
      } else {
        // If still dragging, reset the timeout
        this.resetInactivityTimeout();
      }
    }, 1000); // Adjust the timeout as necessary
  }

  // Processes accumulated changes in the buffer
  processChangesBuffer() {
    if (this.isDragging) {
      // If user is still dragging, delay processing
      this.resetInactivityTimeout();
      return;
    }

    if (this.changesBuffer.length > 0) {
      console.log('Processing changes:', this.changesBuffer);
      this.isLoading = true; // Show loading indicator

      // Copy buffer and clear it to avoid duplicates
      const changesToProcess = [...this.changesBuffer];
      this.changesBuffer = [];

      // Separate changes by type
      const sectionChanges = changesToProcess.filter(change => !change.parentId);
      const noteChanges = changesToProcess.filter(change => change.parentId);

      const requests = [];

      // Sending section changes
      if (sectionChanges.length > 0) {
        sectionChanges.forEach(change => {
          requests.push(this.notesService.changePredefinedNoteSetOrder(change));
        });
      }

      // Sending note changes
      if (noteChanges.length > 0) {
        noteChanges.forEach(change => {
          requests.push(this.notesService.changePredefinedNoteOrder(change));
        });
      }

      if (requests.length > 0) {
        // Execute all requests in parallel
        const sub = forkJoin(requests).subscribe(
          () => {
            this.isLoading = false; // Hide loading indicator
            this.cdr.detectChanges(); // Ensure changes reflect in UI
          },
          error => {
            console.error('Error processing changes:', error);
            this.isLoading = false;
            this.messagingService.sendComponentMessage('notesDisplayWarning', {
              message: 'Error saving changes. Please try again.'
            });
          }
        );
        // Add subscription to cancel if necessary
        this.subscriptions.add(sub);
      } else {
        this.isLoading = false;
      }
    }
  }

  // Handles note set selection
  onDataNoteSetClick(noteSet: PredefinedNoteSetDataModel) {
    this.selectedDataNoteSet = noteSet;
    this.selectedPredefinedNotes = noteSet.dataPredefinedNotes.sort(
      (a, b) => a.NoteOrder - b.NoteOrder
    );
  }

  // Handles note click event
  onNoteClick(note: PredefinedNoteDataModel) {
    this.noteData += note.NoteTemplate + ' ';
  }

  // Handles save operation
  onSave() {
    // Ensure changes are sent before closing
    if (this.changesBuffer.length > 0) {
      this.processChangesBuffer();
    }
    this.dialogRef.close(this.noteData);
  }

  // Handles cancel operation
  onCancel() {
    // Ensure changes are sent before closing
    if (this.changesBuffer.length > 0) {
      this.processChangesBuffer();
    }
    this.dialogRef.close();
  }

    // Handles creation of a new note set
    onNewNoteSetClick() {
      const set = new PredefinedNoteSetDataModel();
      set.NoteSetName = 'New Template';
      set.NoteSetType = 'BUILDER-NOTES';
      set.NoteSetOrder = this.dataNoteSets.length;
  
      this.notesService.savePredefinedNoteBuilderSet(set).subscribe((data) => {
        this.dataNoteSets.push(data);
        // Update local order
        this.dataNoteSets.forEach((noteSet, index) => {
          noteSet.NoteSetOrder = index;
        });
      });
    }
  
    // Handles creation of a new note
    onNewNoteClick() {
      const note = new PredefinedNoteDataModel();
      note.NoteSetId = this.selectedDataNoteSet.NoteSetId;
      note.NoteTemplate = 'New Note';
      note.NoteDescription = 'New Note';
      note.NoteOrder = this.selectedPredefinedNotes.length;
  
      this.notesService.savePredefinedNoteBuilderNote(note).subscribe((data) => {
        this.selectedPredefinedNotes.push(data);
        // Update local order
        this.selectedPredefinedNotes.forEach((note, index) => {
          note.NoteOrder = index;
        });
      });
    }
  
    // Handles deletion of the selected note set
    onDeleteNoteSetClick() {
      this.dialogService.openConfirmDialog(`Are you sure you want to delete ${this.selectedDataNoteSet.NoteSetName}?`).subscribe((result) => {
        if (result === true) {
          this.notesService.deletePredefinedNoteSet(this.selectedDataNoteSet).subscribe(() => {
            const idx = this.dataNoteSets.findIndex(p => p === this.selectedDataNoteSet);
            if (idx > -1) {
              this.dataNoteSets.splice(idx, 1);
              if (this.dataNoteSets.length > 0) {
                this.selectedDataNoteSet = this.dataNoteSets[0];
                this.selectedPredefinedNotes = this.selectedDataNoteSet.dataPredefinedNotes.sort(
                  (a, b) => a.NoteOrder - b.NoteOrder
                );
              } else {
                this.selectedDataNoteSet = null;
                this.selectedPredefinedNotes = [];
              }
              // Update local order
              this.dataNoteSets.forEach((noteSet, index) => {
                noteSet.NoteSetOrder = index;
              });
              // Add to buffer and reset inactivity timeout
              this.resetInactivityTimeout();
            }
          });
        }
      });
    }
  
    // Handles deletion of a note
    onDeleteNoteClick(note: PredefinedNoteDataModel) {
      this.dialogService.openConfirmDialog(`Are you sure you want to delete ${note.NoteTemplate}?`).subscribe((result) => {
        if (result === true) {
          this.notesService.deletePredefinedNote(note).subscribe(() => {
            const idx = this.selectedPredefinedNotes.findIndex(p => p === note);
            if (idx > -1) {
              this.selectedPredefinedNotes.splice(idx, 1);
              // Update local order
              this.selectedPredefinedNotes.forEach((note, index) => {
                note.NoteOrder = index;
              });
              // Add to buffer and reset inactivity timeout
              this.resetInactivityTimeout();
            }
          });
        }
      });
    }
  
    // Checks if the note is currently editable
    isEditableNote(note: PredefinedNoteDataModel): boolean {
      return this.editableNote === note;
    }
  
    // Handles clicking outside the editable note
    onEditableNoteClickOutside(note: PredefinedNoteDataModel) {
      if (note === this.editableNote) {
        this.notesService.savePredefinedNoteBuilderNote(this.editableNote).subscribe(() => {
          this.editableNote = null;
        });
      }
    }
  
    // Handles completion of editing an editable note
    onEditableNoteInputFinished(event) {
      if (this.editableNote) {
        this.notesService.savePredefinedNoteBuilderNote(this.editableNote).subscribe(() => {
          // Actions after saving
        });
      }
    }
  
    // Handles clicking on a note to edit
    onEditNoteClick(note: PredefinedNoteDataModel) {
      this.editableNote = note;
    }
  
    // Handles completion of editing the note set name
    onNoteSetEditInputFinished(event) {
      this.selectedDataNoteSet.NoteSetName = event;
      this.notesService.savePredefinedNoteBuilderSet(this.selectedDataNoteSet).subscribe(() => {
        // Actions after saving
      });
    }
  
    // Checks if a note set is currently selected
    isNoteSetSelected(noteSet: PredefinedNoteSetDataModel): boolean {
      return noteSet === this.selectedDataNoteSet;
    }
  
    // Checks if a note is currently selected
    isNoteSelected(note: PredefinedNoteDataModel): boolean {
      return this.selectedNotes.includes(note);
    }
  }
  
